import { FC, useEffect, useState } from 'react';
import { Button, Typography, Alert } from '@mui/material';
import React from 'react';
import styled from '@emotion/styled';
import { JSONContent } from '@tiptap/core';

interface DiffEntry {
  type: 'added' | 'removed' | 'modified';
  nodeType: string;
  nodeId?: string;
  oldText?: string;
  newText?: string;
}

function getInlineNodeText(node: JSONContent, nodeLabels?: Map<string, string>): string {
  switch (node.type) {
    case 'internalLink':
      return (node.attrs?.id && nodeLabels?.get(node.attrs.id)) ?? '';
    case 'guideLink':
      return node.attrs?.docName ?? '';
    case 'standardLink':
      return node.attrs?.standardName ?? '';
    default:
      return '';
  }
}

function getNodeText(node: JSONContent, nodeLabels?: Map<string, string>): string {
  if (!node) return '';
  if (node.text) return node.text;
  const inlineText = getInlineNodeText(node, nodeLabels);
  if (inlineText) return inlineText;
  if (node.content) return node.content.map((n) => getNodeText(n, nodeLabels)).join('');
  return '';
}

function getNodeLabel(nodeType?: string): string {
  switch (nodeType) {
    case 'heading':
      return 'Overskrift';
    case 'paragraph':
      return 'Avsnitt';
    case 'bulletList':
      return 'Punktliste';
    case 'orderedList':
      return 'Nummerert liste';
    case 'imageFigure':
      return 'Figur';
    case 'tableFigure':
      return 'Tabell';
    case 'chartFigure':
      return 'Diagramfigur';
    case 'app':
      return 'App';
    default:
      return nodeType || '';
  }
}

// Node types where we check attrs for changes — these have content-meaningful attrs
// (as opposed to formatting-only attrs like textAlign/indent on paragraphs)
const contentAttrTypes = new Set(['imageFigure', 'tableFigure', 'chartFigure', 'app', 'tableCell', 'tableHeader']);

function normalizeAttrValue(v: unknown): string {
  if (v == null) return '';
  return JSON.stringify(v);
}

function attrsChanged(a: Record<string, unknown> | undefined, b: Record<string, unknown> | undefined): boolean {
  // Only compare attrs present in both — attrs in only one side are serialization defaults
  const aKeys = Object.keys(a ?? {});
  const bKeys = new Set(Object.keys(b ?? {}));
  for (const key of aKeys) {
    if (key === 'id' || !bKeys.has(key)) continue;
    if (normalizeAttrValue(a?.[key]) !== normalizeAttrValue(b?.[key])) return true;
  }
  return false;
}

// Recursively check for attr changes, but only on node types with content-meaningful attrs
function deepContentAttrsChanged(a: JSONContent, b: JSONContent): boolean {
  if (contentAttrTypes.has(a.type || '') && attrsChanged(a.attrs, b.attrs)) return true;
  const aChildren = a.content ?? [];
  const bChildren = b.content ?? [];
  const len = Math.min(aChildren.length, bChildren.length);
  for (let i = 0; i < len; i++) {
    if (deepContentAttrsChanged(aChildren[i], bChildren[i])) return true;
  }
  return false;
}

function computeCompactDiff(savedContent: JSONContent, currentContent: JSONContent, nodeLabels?: Map<string, string>): DiffEntry[] {
  const savedNodes = savedContent?.content || [];
  const currentNodes = currentContent?.content || [];

  const savedById = new Map<string, JSONContent>();
  for (const n of savedNodes) {
    if (n.attrs?.id) savedById.set(n.attrs.id, n);
  }

  const currentById = new Set<string>();
  for (const n of currentNodes) {
    if (n.attrs?.id) currentById.add(n.attrs.id);
  }

  const entries: DiffEntry[] = [];
  const processedSavedIds = new Set<string>();

  for (const node of currentNodes) {
    const id = node.attrs?.id;
    if (!id) continue;

    const savedNode = savedById.get(id);
    if (!savedNode) {
      const text = getNodeText(node, nodeLabels);
      entries.push({ type: 'added', nodeType: node.type || '', nodeId: id, newText: text || undefined });
    } else {
      processedSavedIds.add(id);
      const oldText = getNodeText(savedNode, nodeLabels);
      const newText = getNodeText(node, nodeLabels);
      if (oldText !== newText) {
        entries.push({ type: 'modified', nodeType: node.type || '', nodeId: id, oldText, newText });
      } else if (deepContentAttrsChanged(savedNode, node)) {
        entries.push({ type: 'modified', nodeType: node.type || '', nodeId: id });
      }
    }
  }

  for (const node of savedNodes) {
    const id = node.attrs?.id;
    if (id && !processedSavedIds.has(id) && !currentById.has(id)) {
      const text = getNodeText(node, nodeLabels);
      entries.push({ type: 'removed', nodeType: node.type || '', nodeId: id, oldText: text || undefined });
    }
  }

  return entries;
}

const StyledDiffContainer = styled.div`
  font-size: 0.875rem;
`;

const StyledDiffEntry = styled.div<{ diffType: 'added' | 'removed' | 'modified' }>`
  padding: 6px 12px;
  margin-top: 6px;
  border-left: 3px solid;
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
  ${({ diffType }) => {
    switch (diffType) {
      case 'added':
        return 'background: #e6ffec; border-left-color: #2da44e;';
      case 'removed':
        return 'background: #ffebe9; border-left-color: #cf222e;';
      case 'modified':
        return 'background: #fff8c5; border-left-color: #bf8700;';
    }
  }}
`;

const StyledOldText = styled.span`
  color: #82071e;
  text-decoration: line-through;
`;

const StyledNewText = styled.span`
  color: #1a7f37;
`;

const TRUNCATED_MAX_HEIGHT = 350;

export const TiptapDocumentDiff: FC<{
  savedContent: JSONContent;
  currentContent: JSONContent;
  label?: React.ReactNode;
  nodeLabels?: Map<string, string>;
}> = ({ savedContent, currentContent, label, nodeLabels }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const diffEntries = React.useMemo(() => computeCompactDiff(savedContent, currentContent, nodeLabels), [savedContent, currentContent, nodeLabels]);

  useEffect(() => {
    if (containerRef.current) {
      setIsTruncated(containerRef.current.scrollHeight > TRUNCATED_MAX_HEIGHT);
    }
  }, [diffEntries]);

  return (
    <div>
      {label}
      {diffEntries.length === 0 && <Alert severity="info">Ingen tekstendringer funnet (endringene kan være i formatering eller struktur)</Alert>}
      {diffEntries.length > 0 && (
        <>
          <StyledDiffContainer
            ref={containerRef}
            style={!isExpanded && isTruncated ? { maxHeight: TRUNCATED_MAX_HEIGHT, overflow: 'hidden' } : undefined}>
            {diffEntries.map((entry, i) => (
              <StyledDiffEntry key={i} diffType={entry.type}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {entry.type === 'added' && '+ Lagt til'}
                  {entry.type === 'removed' && '− Fjernet'}
                  {entry.type === 'modified' && '~ Endret'}
                  {' · '}
                  {(entry.nodeId && nodeLabels?.get(entry.nodeId)) ?? getNodeLabel(entry.nodeType)}
                </Typography>
                {(entry.oldText || entry.newText) && (
                  <div style={{ marginTop: 2 }}>
                    {entry.type === 'added' && <StyledNewText>{entry.newText}</StyledNewText>}
                    {entry.type === 'removed' && <StyledOldText>{entry.oldText}</StyledOldText>}
                    {entry.type === 'modified' && (
                      <>
                        <div>
                          <StyledOldText>{entry.oldText}</StyledOldText>
                        </div>
                        <div>
                          <StyledNewText>{entry.newText}</StyledNewText>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </StyledDiffEntry>
            ))}
          </StyledDiffContainer>
          {isTruncated && (
            <Button size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ mt: 0.5 }}>
              {isExpanded ? 'Vis mindre' : `Vis alle endringer`}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
